import { calgaryDateString } from '../../../../../../utils/calgaryTime';
import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch, faCheck, faArrowTrendUp, faArrowTrendDown } from "@fortawesome/free-solid-svg-icons";
import FinancialService from "../../../../../../AppSettings/services/FinancialService";
import GenExpensesService from "../../../../../../AppSettings/services/GenExpensesService";
import OppExpService from "../../../../../../AppSettings/services/OppExpService";
import VenExpService from "../../../../../../AppSettings/services/VenExpService";
import FixBillService from "../../../../../../AppSettings/services/FixBillService";
import MatterService from "../../../../../../AppSettings/services/MatterService";
import EmployeeService from "../../../../../../AppSettings/services/EmployeeService";
import AgentsService from "../../../../../../AppSettings/services/AgentsService";
import useActiveEmployeeId from "../../../../../../AppSettings/hooks/useActiveEmployeeId";
import FormEntryMultilineTextbox from "@frontend/Components/Forms/Entry/MultilineTextbox/FormEntryMultilineTextbox";
import FormModal from "@frontend/Components/Modals/FormModal";
import "./LogTransactionModal.css";

type TransactionCategory = "income" | "expense";
type IncomeType = "timeEntry" | "fixedFee" | "otherBilling";
type ExpenseType = "general" | "operating" | "vendorServices" | "fixedBills";

interface LogTransactionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  matterId?: string;
}

const LogTransactionModal: React.FC<LogTransactionModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  matterId,
}) => {
  const employeeId = useActiveEmployeeId();
  
  // Primary category selection
  const [category, setCategory] = useState<TransactionCategory>("income");
  
  // Sub-type selections
  const [incomeType, setIncomeType] = useState<IncomeType>("timeEntry");
  const [expenseType, setExpenseType] = useState<ExpenseType>("general");
  
  // Common state
  const [matter, setMatter] = useState<any>(null);
  const [employee, setEmployee] = useState<any>(null);
  const [agents, setAgents] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Common fields
  const [date, setDate] = useState(calgaryDateString());
  
  // Income: Time Entry fields
  const [hours, setHours] = useState("");
  const [hourlyRate, setHourlyRate] = useState("");
  const [workDescription, setWorkDescription] = useState("");
  
  // Income: Fixed Fee fields
  const [feeCode, setFeeCode] = useState("");
  const [narrative, setNarrative] = useState("");
  const [feeAmount, setFeeAmount] = useState("");
  
  // Income: Other Billing fields
  const [otherDescription, setOtherDescription] = useState("");
  const [otherAmount, setOtherAmount] = useState("");
  const [otherTaxPercent, setOtherTaxPercent] = useState("0");
  
  // Expense fields
  const [selectedAgent, setSelectedAgent] = useState<any>(null);
  const [agentSearch, setAgentSearch] = useState("");
  const [expenseDescription, setExpenseDescription] = useState("");
  const [expenseAmount, setExpenseAmount] = useState("");
  const [expenseTaxPercent, setExpenseTaxPercent] = useState("0");
  const [showAgentDropdown, setShowAgentDropdown] = useState(false);

  const agentInputRef = useRef<HTMLInputElement>(null);
  const agentDropdownRef = useRef<HTMLDivElement>(null);

  // Fetch matter, employee, and agents data
  useEffect(() => {
    const fetchData = async () => {
      if (matterId) {
        try {
          const matterData = await MatterService.getMatterName(matterId);
          if (matterData) {
            setMatter({
              matterid: matterId,
              mattername: matterData.mattername || matterId,
            });
          }
        } catch (error) {
          console.error("Error fetching matter:", error);
        }
      }

      if (employeeId) {
        try {
          const employeeData = await EmployeeService.getEmployeeById(employeeId);
          setEmployee(employeeData);
          if (employeeData?.billedrate) {
            setHourlyRate(employeeData.billedrate.toString());
          }
        } catch (error) {
          console.error("Error fetching employee:", error);
        }
      }

      try {
        const agentsData = await AgentsService.getAgents();
        setAgents(agentsData || []);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };

    if (isOpen) {
      fetchData();
    }
  }, [isOpen, matterId, employeeId]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setCategory("income");
      setIncomeType("timeEntry");
      setExpenseType("general");
      setHours("");
      setHourlyRate(employee?.billedrate?.toString() || "");
      setWorkDescription("");
      setFeeCode("");
      setNarrative("");
      setFeeAmount("");
      setOtherDescription("");
      setOtherAmount("");
      setOtherTaxPercent("0");
      setSelectedAgent(null);
      setAgentSearch("");
      setExpenseDescription("");
      setExpenseAmount("");
      setExpenseTaxPercent("0");
      setDate(calgaryDateString());
      setError("");
    }
  }, [isOpen, employee]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        agentDropdownRef.current &&
        agentInputRef.current &&
        !agentDropdownRef.current.contains(event.target as Node) &&
        !agentInputRef.current.contains(event.target as Node)
      ) {
        setShowAgentDropdown(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const getAgentDisplayName = (agent: any): string => {
    let displayName = "";
    if (agent.FirstName && agent.LastName) {
      displayName = `${agent.FirstName} ${agent.LastName}`;
    } else if (agent.FirstName && !agent.LastName) {
      displayName = agent.FirstName;
    } else if (!agent.FirstName && agent.LastName) {
      displayName = agent.LastName;
    } else if (agent.ContactName) {
      displayName = agent.ContactName;
    } else {
      displayName = "Unknown Agent";
    }
    return `${displayName}${agent.Company ? ` - ${agent.Company}` : ""}`;
  };

  const filteredAgents = agents.filter((agent) =>
    getAgentDisplayName(agent)
      .toLowerCase()
      .includes(agentSearch.toLowerCase())
  );

  const handleAgentSelect = (agent: any) => {
    setSelectedAgent(agent);
    setAgentSearch(getAgentDisplayName(agent));
    setShowAgentDropdown(false);
  };

  const formatTimeRecorded = (time: string): string => {
    const totalMinutes = parseFloat(time) * 60;
    const hoursVal = Math.floor(totalMinutes / 60);
    const minutes = Math.floor(totalMinutes % 60);
    return `${hoursVal < 10 ? "0" + hoursVal : hoursVal}:${
      minutes < 10 ? "0" + minutes : minutes
    }:00`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (!matterId || !matter) {
        setError("Matter information is missing");
        setLoading(false);
        return;
      }

      if (category === "income") {
        // Handle Income transactions
        if (incomeType === "timeEntry") {
          if (!employee) {
            setError("Employee data is missing");
            setLoading(false);
            return;
          }

          const formattedTime = formatTimeRecorded(hours);

          await FinancialService.logTime({
            time_recorded: formattedTime,
            date_recorded: date,
            notes: workDescription,
            employeeid: employee.employeeid,
            matterid: matterId,
          });
        } else if (incomeType === "fixedFee") {
          if (!feeCode || !narrative || !feeAmount) {
            setError("Please fill in all required fields");
            setLoading(false);
            return;
          }

          await FinancialService.addAssignedFixedFees({
            fee_code: feeCode,
            narrative: narrative,
            fees: parseFloat(feeAmount),
            date_added: date,
            matterid: matterId,
          });
        } else if (incomeType === "otherBilling") {
          if (!otherDescription || !otherAmount || !employeeId) {
            setError("Please fill in all required fields");
            setLoading(false);
            return;
          }

          await FinancialService.addOtherBilling({
            description: otherDescription,
            amount: parseFloat(otherAmount),
            date: date,
            employeeid: employeeId,
            matterid: matterId,
            tax_percent: parseFloat(otherTaxPercent) || 0,
          });
        }
      } else {
        // Handle Expense transactions
        if (!selectedAgent) {
          setError("Please select an agent");
          setLoading(false);
          return;
        }

        if (!expenseDescription || !expenseAmount) {
          setError("Please fill in all required fields");
          setLoading(false);
          return;
        }

        const formData: any = {
          description: expenseDescription,
          amount: parseFloat(expenseAmount),
          tax_percent: parseFloat(expenseTaxPercent) || 0,
          date_added: date,
          agentid: selectedAgent.AgentID,
          matterid: matterId,
        };

        if (expenseType === "general") {
          formData.invoiceid = 0;
          await GenExpensesService.addGenExpense(formData);
        } else if (expenseType === "operating") {
          await OppExpService.addOppExpense(formData);
        } else if (expenseType === "vendorServices") {
          await VenExpService.addVenExpense(formData);
        } else if (expenseType === "fixedBills") {
          await FixBillService.addFixBillExpense(formData);
        }
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error("Error adding transaction:", error);
      setError(error.message || "Failed to add transaction. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title="Log Transaction"
      size="medium"
    >
      {/* Primary Category Toggle */}
      <div className="log-txn-category-toggle">
        <button
          type="button"
          className={`log-txn-category-btn ${category === "income" ? "active income" : ""}`}
          onClick={() => setCategory("income")}
        >
          <FontAwesomeIcon icon={faArrowTrendUp} />
          <span>Income</span>
        </button>
        <button
          type="button"
          className={`log-txn-category-btn ${category === "expense" ? "active expense" : ""}`}
          onClick={() => setCategory("expense")}
        >
          <FontAwesomeIcon icon={faArrowTrendDown} />
          <span>Expense</span>
        </button>
      </div>

      {/* Sub-type Tabs */}
      {category === "income" ? (
        <div className="log-txn-type-tabs">
          <button
            type="button"
            className={`log-txn-tab ${incomeType === "timeEntry" ? "active" : ""}`}
            onClick={() => setIncomeType("timeEntry")}
          >
            Time Entry
          </button>
          <button
            type="button"
            className={`log-txn-tab ${incomeType === "fixedFee" ? "active" : ""}`}
            onClick={() => setIncomeType("fixedFee")}
          >
            Fixed Fee
          </button>
          <button
            type="button"
            className={`log-txn-tab ${incomeType === "otherBilling" ? "active" : ""}`}
            onClick={() => setIncomeType("otherBilling")}
          >
            Other
          </button>
        </div>
      ) : (
        <div className="log-txn-type-tabs">
          <button
            type="button"
            className={`log-txn-tab ${expenseType === "general" ? "active" : ""}`}
            onClick={() => setExpenseType("general")}
          >
            General
          </button>
          <button
            type="button"
            className={`log-txn-tab ${expenseType === "operating" ? "active" : ""}`}
            onClick={() => setExpenseType("operating")}
          >
            Operating
          </button>
          <button
            type="button"
            className={`log-txn-tab ${expenseType === "vendorServices" ? "active" : ""}`}
            onClick={() => setExpenseType("vendorServices")}
          >
            Vendor
          </button>
          <button
            type="button"
            className={`log-txn-tab ${expenseType === "fixedBills" ? "active" : ""}`}
            onClick={() => setExpenseType("fixedBills")}
          >
            Fixed Bills
          </button>
        </div>
      )}

      {error && <div className="log-txn-error">{error}</div>}

      <form onSubmit={handleSubmit} className="log-txn-form">
        {/* Common Fields */}
        <div className="log-txn-field">
          <label>Date</label>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            required
          />
        </div>

        <div className="log-txn-field">
          <label>Matter</label>
          <input
            type="text"
            value={matter ? `${matter.matterid}: ${matter.mattername || ""}` : "Loading..."}
            disabled
            className="log-txn-disabled-input"
          />
        </div>

        {/* Income: Time Entry Fields */}
        {category === "income" && incomeType === "timeEntry" && (
          <>
            <div className="log-txn-field">
              <label>Hours</label>
              <input
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={hours}
                onChange={(e) => setHours(e.target.value)}
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Hourly Rate</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={hourlyRate}
                onChange={(e) => setHourlyRate(e.target.value)}
                required
              />
              <p className="log-txn-hint">
                Auto-populated from your profile.
              </p>
            </div>

            <div className="log-txn-field">
              <label>Work Description</label>
              <FormEntryMultilineTextbox
                name="workDescription"
                value={workDescription}
                onChange={(e) => setWorkDescription(e.target.value)}
                placeholder="Describe the work performed..."
                rows={3}
              />
            </div>
          </>
        )}

        {/* Income: Fixed Fee Fields */}
        {category === "income" && incomeType === "fixedFee" && (
          <>
            <div className="log-txn-field">
              <label>Fee Code</label>
              <input
                type="text"
                value={feeCode}
                onChange={(e) => setFeeCode(e.target.value)}
                placeholder="Enter fee code"
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Narrative</label>
              <input
                type="text"
                value={narrative}
                onChange={(e) => setNarrative(e.target.value)}
                placeholder="Enter narrative"
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Fee Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={feeAmount}
                onChange={(e) => setFeeAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>
          </>
        )}

        {/* Income: Other Billing Fields */}
        {category === "income" && incomeType === "otherBilling" && (
          <>
            <div className="log-txn-field">
              <label>Description</label>
              <input
                type="text"
                value={otherDescription}
                onChange={(e) => setOtherDescription(e.target.value)}
                placeholder="Enter billing description"
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={otherAmount}
                onChange={(e) => setOtherAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Tax Percent (Optional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={otherTaxPercent}
                onChange={(e) => setOtherTaxPercent(e.target.value)}
                placeholder="0"
              />
            </div>
          </>
        )}

        {/* Expense Fields */}
        {category === "expense" && (
          <>
            <div className="log-txn-field">
              <label>Agent</label>
              <div className="log-txn-search-container">
                <input
                  ref={agentInputRef}
                  type="text"
                  placeholder="Search agents..."
                  value={agentSearch}
                  onChange={(e) => {
                    setAgentSearch(e.target.value);
                    setShowAgentDropdown(true);
                  }}
                  onFocus={() => setShowAgentDropdown(true)}
                  required
                />
                <FontAwesomeIcon icon={faSearch} className="log-txn-search-icon" />
                {showAgentDropdown && filteredAgents.length > 0 && (
                  <div ref={agentDropdownRef} className="log-txn-dropdown">
                    {filteredAgents.slice(0, 10).map((agent) => (
                      <div
                        key={agent.AgentID}
                        className="log-txn-dropdown-item"
                        onClick={() => handleAgentSelect(agent)}
                      >
                        {getAgentDisplayName(agent)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            <div className="log-txn-field">
              <label>Description</label>
              <FormEntryMultilineTextbox
                name="expenseDescription"
                value={expenseDescription}
                onChange={(e) => setExpenseDescription(e.target.value)}
                placeholder="Enter expense description..."
                rows={3}
              />
            </div>

            <div className="log-txn-field">
              <label>Amount</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={expenseAmount}
                onChange={(e) => setExpenseAmount(e.target.value)}
                placeholder="0.00"
                required
              />
            </div>

            <div className="log-txn-field">
              <label>Tax Percent (Optional)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={expenseTaxPercent}
                onChange={(e) => setExpenseTaxPercent(e.target.value)}
                placeholder="0"
              />
            </div>
          </>
        )}

        {/* Action Buttons */}
        <div className="log-txn-actions">
          <button
            type="button"
            className="log-txn-cancel"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`log-txn-save ${category}`}
            disabled={loading}
          >
            <FontAwesomeIcon icon={faCheck} />
            {loading ? "Saving..." : "Save Transaction"}
          </button>
        </div>
      </form>
    </FormModal>
  );
};

export default LogTransactionModal;
